/**
 * Stamp each `<table>` with the nearest preceding `<h1>`-`<h6>` text as
 * `properties.dataInferredTitle`, then remove that heading so it does not
 * render twice — once above the table and once in the toolbar. The table
 * override receives no parent context, so the lift happens at the rehype layer
 * and the renderer reads the title back.
 *
 * Lives here rather than beside the renderer because it is a pure hast
 * transform with no React in it, and the verification harness runs the same
 * pipeline outside Vite.
 *
 * GFM tables only appear as root-level children, so no recursion is needed.
 */
import type { ElementContent as HastContent, Root as HastRoot } from "hast";

import { getEditorialProvenance, markEditorial } from "./editorial-provenance";
import { inferTitleFromSiblings } from "./parse-markdown-table";

export function rehypeInferTableTitle() {
    return (tree: HastRoot) => {
        // `tree.children` is `RootContent[]`, a superset of `ElementContent[]`
        // (it includes `Doctype`). The cast is safe; `inferTitleFromSiblings`
        // only inspects element and text nodes.
        const children = (tree.children ?? []) as HastContent[];
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (child.type !== "element" || child.tagName !== "table") {
                continue;
            }
            // A table this pass already lifted has consumed its heading, and
            // re-running would take the NEXT heading up the document and
            // overwrite the correct title with it — measured on the
            // kitchen-sink corpus, where a second pass replaced "Limit
            // Comparison" with "Detail" and removed an unrelated heading. The
            // pass has to be a no-op on its own output.
            //
            // Keyed on provenance, NOT on the presence of `dataInferredTitle`:
            // that attribute is authorable from raw HTML and is not in a
            // scrubbed namespace, so skipping on it would let a message pin a
            // title of its own choosing by writing one. Possession of the mark
            // is the only thing that proves this pass put it there.
            if (getEditorialProvenance(child) !== undefined) continue;

            const found = inferTitleFromSiblings(children, i);
            if (!found) continue;
            child.properties = child.properties ?? {};
            child.properties.dataInferredTitle = found.title;
            // `structural-mark`, not a minted kind: this records a fact onto an
            // authored node. Marking it minted would turn the table into
            // editorial output and tell the downstream passes to treat a
            // source-model node as ours.
            markEditorial(child, { kind: "structural-mark" });
            // Splicing an earlier element shifts every later sibling down by
            // one. Decrement `i` so the next iteration's `i++` lands on the
            // table's original next sibling instead of skipping past it —
            // matters when two tables are adjacent.
            children.splice(found.headingIndex, 1);
            i--;
        }
    };
}
